import React from "react"
import { useTranslation } from "react-i18next"
import { AxiosError } from "axios"
import { useNavigate } from "react-router-dom"

import FormElement from "components/Form/FormElement"
import { ACCEPTED_BIKE_FLEET, fleetManagementChoice } from "../constant"
import { hasAccessToTheBikeFleet, isNotEmpty } from "utils"
import { useAlert, useAuth, useTheme } from "hooks"
import { addStore } from "../api/bikeFleetApi"
import { http } from "services/http"

export const FormBikeFleetChoice: React.FC = () => {
	const { t } = useTranslation()
	const [formFields, setFormFields] = React.useState(null)
	const { alert } = useAlert()
	const navigate = useNavigate()

	const [showLocalErrors, setShowLocalErrors] = React.useState(false)
	const {
		currentTheme: { id_api },
	} = useTheme()

	const {
		entity: { data_operators },
	} = useAuth()

	const isAuthorizedAccessFleetPage = hasAccessToTheBikeFleet(data_operators, id_api)

	React.useEffect(() => {
		if (!isAuthorizedAccessFleetPage) navigate("/")
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAuthorizedAccessFleetPage])

	const { fetchEntity, logout, isConnectedFromSupport } = useAuth()

	const handleSubmit = async (event: React.SyntheticEvent) => {
		event.preventDefault()
		if (isConnectedFromSupport)
			return alert("error", t("bikeFleetErrorBoSigninWithRefresh", { ns: "api" }))
		if (!isNotEmpty(formFields)) {
			setShowLocalErrors(true)
		} else {
			setShowLocalErrors(false)
			try {
				const { res: responseAddStore, getError } = await addStore({
					operator: id_api,
					is_fleet: formFields === ACCEPTED_BIKE_FLEET,
				})
				if (getError() || !responseAddStore) throw getError()
				await http.signinWithRefreshTokenForBikeFleet({ alert, logout })
				await fetchEntity(responseAddStore?.entity_id)
			} catch (err) {
				const erroMsg = (err as unknown as AxiosError)?.message?.toString()
				if (erroMsg) alert("error", erroMsg)
			}
		}
	}

	return (
		<div className="px-8">
			<div>
				<form data-cy="form-bike-fleet" className="c-card p-5 md:p-12 mt-4">
					<div className="lg:max-w-[66.67%]">
						<h1 className="c-h2 font-semibold mb-7 uppercase">
							{t("bikeFleet.form.title", { ns: "bike" })}
						</h1>
						<p className="mt-8 whitespace-normal">
							{t("bikeFleet.form.presentation", { ns: "bike" })}
						</p>
						<p className="mt-8">{t("bikeFleet.form.sentence", { ns: "bike" })}</p>
					</div>
					<div className="mt-8">
						<div className="lg:max-w-[66.67%]">
							<div>
								<div className="font-bold">
									{t("bikeFleet.form.question", { ns: "bike" })}
								</div>
								<FormElement
									value={formFields}
									name="bikeFleet"
									type="radio"
									options={fleetManagementChoice.map((option) => ({
										...option,
										label: t(option.label, { ns: "bike" }),
									}))}
									className="w-full mt-2"
									classNameOptionsContainer="w-full lg:whitespace-nowrap flex gap-2 lg:flex-row flex-col"
									classNameOption="flex-1 flex items-center"
									required={true}
									showErrors={showLocalErrors}
									onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
										setFormFields(event.target.value)
									}
								/>
							</div>
							<p className="mt-8">
								{t("bikeFleet.form.afterChoice", { ns: "bike" })}
							</p>
							<p className="mt-8">
								{t("bikeFleet.form.explication", { ns: "bike" })}
							</p>
							<p className="font-bold">
								{t("bikeFleet.form.explicationRefuse", { ns: "bike" })}
							</p>
						</div>
						<div className="flex justify-end mt-8">
							<button
								data-cy="confirm-bike-fleet"
								onClick={handleSubmit}
								className="c-btn c-btn--primary-bordered px-7 whitespace-nowrap"
							>
								{t("global.confirm")}
							</button>
						</div>
					</div>
				</form>
			</div>
		</div>
	)
}
